import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { LoginLogsService } from '../../shared/components/layout/dashboard/loginLogs/login-logs.service';

@Injectable({
    providedIn: 'root',
})
export class InactivityService {
    private mouseMoveHandler = () => this.resetInactivityTimer();
    private keyDownHandler = () => this.resetInactivityTimer();
    private listenersActive = false;
    private inactivityTimer: any;
    private warningTimer: any;
    // Removed listenersAdded guard to always add listeners
    private readonly timeoutDuration = 20 * 60 * 1000; // 20 minutes in ms
    private hasInteracted = false; // To track whether the user clicked "Stay Logged In"

    constructor(
        private router: Router,
        private ngZone: NgZone,
        private authService: AuthService,
        private toast: ToastService,
        private loginLogsService: LoginLogsService
    ) {
        // Initialize the inactivity timer if the user is logged in and has a valid token
        if (this.isLoggedIn() && this.hasAuthToken()) {
            this.startInactivityTimer();
            this.addActivityListeners();
        }
    }

    // Check if the user is logged in
    private isLoggedIn(): boolean {
        return localStorage.getItem('isLoggedIn') === 'true';
    }

    // Check if there is a valid auth token in session storage
    private hasAuthToken(): boolean {
        return !!sessionStorage.getItem('authToken');
    }

    // Start inactivity timer (1 minute)
    public startInactivityTimer() {
        // Only run if user is logged in and has a valid auth token
        if (!this.isLoggedIn() || !this.hasAuthToken()) {
            this.stopInactivityTimer();
            return;
        }
        // Add listeners for user activity only if not already active
        if (!this.listenersActive) {
            this.addActivityListeners();
            this.listenersActive = true;
        }
        clearTimeout(this.inactivityTimer);
        clearTimeout(this.warningTimer);
        this.ngZone.runOutsideAngular(() => {
            // console.log('Starting inactivity timer');
            // Show warning 10 seconds before session expires
            this.hasInteracted = false; // Reset interaction flag on timer start
            this.warningTimer = setTimeout(() => {
                this.showWarning(); // Show warning pop-up
            });

            // Log out after 1 minute of inactivity, if no interaction has occurred
            this.inactivityTimer = setTimeout(() => {
                if (!this.hasInteracted) {
                    this.logout(); // Log out if no interaction
                }
            }, this.timeoutDuration);
        });
    }

    // Reset the inactivity timer to 1 minute on user interaction
    private resetInactivityTimer() {
        // console.log('User interaction detected, resetting inactivity timer.');
        clearTimeout(this.inactivityTimer);
        clearTimeout(this.warningTimer);
        this.hasInteracted = false; // Reset interaction flag
        this.startInactivityTimer(); // Always restart timer on activity
        // No toast to dismiss
    }

    // Add listeners for user activity (mousemove, keydown)
    private addActivityListeners() {
        window.addEventListener('mousemove', this.mouseMoveHandler);
        window.addEventListener('keydown', this.keyDownHandler);
    }

    // Show warning before logout
    private showWarning() {
        // No toast for session expiry warning
    }

    // Handle "Stay Logged In" button click
    private onStayLoggedIn() {
        this.hasInteracted = true; // Mark as interacted
        this.resetInactivityTimer(); // Reset the inactivity timer
        // No toast to dismiss
    }

    // Stop inactivity timer (optional)
    public stopInactivityTimer() {
        clearTimeout(this.inactivityTimer);
        clearTimeout(this.warningTimer);
        // Remove listeners to prevent memory leaks
        if (this.listenersActive) {
            window.removeEventListener('mousemove', this.mouseMoveHandler);
            window.removeEventListener('keydown', this.keyDownHandler);
            this.listenersActive = false;
        }
    }

    // Log out the user
    private logout() {
        const loginLogId = sessionStorage.getItem('loginLogId');
        if (loginLogId) {
            this.loginLogsService.logUpdate(loginLogId, () => {
                this.authService.signOut();
            });
        } else {
            this.authService.signOut();
        }
        // this.toast.show('Session expired. Please log in again.','error',3000);
        // No toast message for inactivity logout
    }

    
}
